/**
 * Admin roles, granular permissions, and the default permission set each role
 * grants. All mirror the Prisma enums so the DB and the app agree on one
 * vocabulary. An admin's effective permissions = their role's defaults, extended
 * by per-admin overrides (see `Admin.permissions`) — use {@link effectivePermissions}.
 */
export const ADMIN_ROLES = ["SUPER_ADMIN", "ADMIN", "MODERATOR", "SUPPORT"] as const;
export type AdminRole = (typeof ADMIN_ROLES)[number];

/**
 * The full permission vocabulary. `requireAdmin(permission)` guards every admin
 * route with one of these; the panel shows/hides features by effective grants.
 */
export const ADMIN_PERMISSIONS = [
  "admins.manage",
  "users.moderate",
  "reports.handle",
  "bans.manage",
  "broadcast.create",
  "broadcast.send",
  "broadcast.cancel",
  "channels.manage",
  "config.manage",
  // Monetization/data platform: feature flags, campaigns, economy adjustments,
  // tournaments, cosmetics, and the analytics rollup reads (Wave 2B).
  "monetization.manage",
  "support.view",
  "support.reply",
  "support.assign",
  "support.close",
  "analytics.view",
  "logs.view",
] as const;
export type AdminPermission = (typeof ADMIN_PERMISSIONS)[number];

/** Role → its default permission set (before per-admin override grants). */
export const ROLE_PERMISSIONS: Record<AdminRole, readonly AdminPermission[]> = {
  // Super-admin holds everything, including managing other admins.
  SUPER_ADMIN: ADMIN_PERMISSIONS,
  // Admin holds everything EXCEPT managing admins.
  ADMIN: ADMIN_PERMISSIONS.filter((p) => p !== "admins.manage"),
  // Support handles tickets and reads analytics. (The in-app broadcast is guarded by
  // `broadcast.send`, which SUPPORT does NOT hold — a dead `inbox.send` permission that only
  // SUPPORT was granted was removed so intent and enforcement can't drift apart.)
  SUPPORT: ["support.view", "support.reply", "support.assign", "support.close", "analytics.view"],
  // Moderator handles users, reports, bans, and reads analytics + logs.
  MODERATOR: ["users.moderate", "reports.handle", "bans.manage", "analytics.view", "logs.view"],
};

/** Whether `role` grants `permission` by default (before per-admin overrides). */
export function roleHasPermission(role: AdminRole, permission: AdminPermission): boolean {
  return ROLE_PERMISSIONS[role].includes(permission);
}

/**
 * An admin's effective permissions: the role defaults UNION any per-admin
 * override grants (deduplicated, filtered to the known vocabulary).
 */
export function effectivePermissions(
  role: AdminRole,
  overrides: readonly string[] = [],
): AdminPermission[] {
  const set = new Set<AdminPermission>(ROLE_PERMISSIONS[role]);
  for (const raw of overrides) {
    if ((ADMIN_PERMISSIONS as readonly string[]).includes(raw)) set.add(raw as AdminPermission);
  }
  return [...set];
}

/** Whether the given role+overrides grant `permission` (the RBAC check). */
export function hasPermission(
  role: AdminRole,
  overrides: readonly string[],
  permission: AdminPermission,
): boolean {
  return roleHasPermission(role, permission) || overrides.includes(permission);
}

// ---------------------------------------------------------------------------
// Broadcasts
// ---------------------------------------------------------------------------

export const BROADCAST_CHANNELS = ["IN_APP", "TELEGRAM_PV"] as const;
export type BroadcastChannel = (typeof BROADCAST_CHANNELS)[number];

export const BROADCAST_TARGETS = ["ALL", "SEGMENT", "SINGLE"] as const;
export type BroadcastTarget = (typeof BROADCAST_TARGETS)[number];

export const BROADCAST_STATUSES = ["DRAFT", "SCHEDULED", "SENDING", "SENT", "CANCELED"] as const;
export type BroadcastStatus = (typeof BROADCAST_STATUSES)[number];

export const DELIVERY_STATUSES = ["PENDING", "SENT", "FAILED", "BLOCKED"] as const;
export type DeliveryStatus = (typeof DELIVERY_STATUSES)[number];

// ---------------------------------------------------------------------------
// In-app inbox
// ---------------------------------------------------------------------------

export const INBOX_SOURCES = ["SYSTEM", "BROADCAST", "SUPPORT"] as const;
export type InboxSource = (typeof INBOX_SOURCES)[number];

// ---------------------------------------------------------------------------
// Support ticketing
// ---------------------------------------------------------------------------

export const SUPPORT_TICKET_STATES = ["OPEN", "PENDING", "RESOLVED"] as const;
export type SupportTicketState = (typeof SUPPORT_TICKET_STATES)[number];

export const SUPPORT_SOURCES = ["APP", "BOT"] as const;
export type SupportSource = (typeof SUPPORT_SOURCES)[number];

export const SUPPORT_DIRECTIONS = ["INBOUND_USER", "OUTBOUND_AGENT"] as const;
export type SupportDirection = (typeof SUPPORT_DIRECTIONS)[number];

// ---------------------------------------------------------------------------
// Moderation
// ---------------------------------------------------------------------------

export const REPORT_REASONS = ["SPAM", "ABUSE", "CHEATING", "INAPPROPRIATE", "OTHER"] as const;
export type ReportReason = (typeof REPORT_REASONS)[number];

export const REPORT_STATUSES = ["OPEN", "RESOLVED", "DISMISSED"] as const;
export type ReportStatus = (typeof REPORT_STATUSES)[number];
